import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import {
  CATEGORIES_BY_AUDIENCE,
  knowledgeGapsApi,
  KnowledgeAudience,
  KnowledgeGap,
  KnowledgeGapStatus,
  KnowledgeCategory,
} from '../../api/knowledge-gaps';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../hooks/useToast';

const statusColors: Record<
  KnowledgeGapStatus,
  'success' | 'danger' | 'warning' | 'info' | 'neutral'
> = {
  pending: 'warning',
  in_review: 'info',
  approved: 'success',
  rejected: 'danger',
};

const STATUS_OPTIONS: KnowledgeGapStatus[] = ['pending', 'in_review', 'approved', 'rejected'];

const AUDIENCE_OPTIONS: KnowledgeAudience[] = ['consumer', 'business', 'both'];

const audienceLabel: Record<KnowledgeAudience, string> = {
  consumer: 'Personal app',
  business: 'Business app',
  both: 'Shared',
};

const formatCategory = (c: string) =>
  c
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const KnowledgeGaps: React.FC = () => {
  usePage('Knowledge Gaps');
  const toast = useToast();

  const [data, setData] = useState<KnowledgeGap[]>([]);
  const [meta, setMeta] = useState<{ page: number; totalPages: number } | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<KnowledgeGapStatus | ''>('pending');
  const [audienceFilter, setAudienceFilter] = useState<KnowledgeAudience | ''>('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<KnowledgeGap | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await knowledgeGapsApi.list({
        page,
        limit: 20,
        status: statusFilter || undefined,
        audience: audienceFilter || undefined,
      });
      setData(result.items);
      setMeta(result.meta);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, audienceFilter]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, audienceFilter]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filterSelectStyle: React.CSSProperties = {
    height: 38,
    padding: '0 32px 0 12px',
    fontSize: 13,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-700)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
    cursor: 'pointer',
  };

  return (
    <>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as KnowledgeGapStatus | '')}
          style={filterSelectStyle}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {formatCategory(s)}
            </option>
          ))}
        </select>
        <select
          value={audienceFilter}
          onChange={(e) => setAudienceFilter(e.target.value as KnowledgeAudience | '')}
          style={filterSelectStyle}
        >
          <option value="">Both apps</option>
          {AUDIENCE_OPTIONS.map((a) => (
            <option key={a} value={a}>
              {audienceLabel[a]}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
          Questions the AI assistant flagged as lacking grounded knowledge for. Approving
          one writes it into the AI's knowledge base — future users asking the same thing
          get a grounded answer.
        </span>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner size={28} />
        </div>
      ) : (
        <Card padding={0}>
          <Table
            columns={[
              {
                key: 'userQuestion',
                header: 'Question',
                render: (r: KnowledgeGap) => (
                  <div>
                    <div style={{ color: 'var(--neutral-800)', maxWidth: 420 }}>
                      {r.userQuestion}
                    </div>
                    {r.occurrenceCount > 1 && (
                      <div style={{ fontSize: 11, color: 'var(--neutral-500)', marginTop: 2 }}>
                        Asked {r.occurrenceCount} times
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'audience',
                header: 'Asked in',
                render: (r: KnowledgeGap) => (
                  <span style={{ fontSize: 13, color: 'var(--neutral-700)' }}>
                    {audienceLabel[r.audience] ?? r.audience}
                  </span>
                ),
              },
              {
                key: 'categoryGuess',
                header: 'Category',
                render: (r: KnowledgeGap) =>
                  r.categoryGuess ? (
                    <span style={{ fontSize: 13, color: 'var(--neutral-700)' }}>
                      {formatCategory(r.categoryGuess)}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--neutral-500)', fontSize: 12 }}>—</span>
                  ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (r: KnowledgeGap) => (
                  <Badge variant={statusColors[r.status]}>{formatCategory(r.status)}</Badge>
                ),
              },
              {
                key: 'createdAt',
                header: 'Flagged',
                render: (r: KnowledgeGap) => formatDate(r.createdAt),
              },
              {
                key: 'action',
                header: '',
                render: (r: KnowledgeGap) => (
                  <Button size="sm" variant="secondary" onClick={() => setSelected(r)}>
                    Review
                  </Button>
                ),
              },
            ]}
            data={data}
            emptyMessage="No knowledge gaps flagged"
          />
          {meta && meta.totalPages > 1 && (
            <div style={{ padding: 16 }}>
              <Pagination
                page={meta.page}
                totalPages={meta.totalPages}
                total={0}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>
      )}

      {selected && (
        <ReviewModal
          gap={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            void fetchData();
          }}
          toast={toast}
        />
      )}
    </>
  );
};

const ReviewModal: React.FC<{
  gap: KnowledgeGap;
  onClose: () => void;
  onSaved: () => void;
  toast: { success: (msg: string) => void; error: (err: unknown, fallback?: string) => void };
}> = ({ gap, onClose, onSaved, toast }) => {
  const alreadyResolved = gap.status === 'approved' || gap.status === 'rejected';
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve');
  const [title, setTitle] = useState('');
  // Only the categories that make sense for the app the question came from.
  // The backend ingests at the gap's own audience regardless of what is
  // picked here, so offering `personal_finance` for a company's VAT question
  // would not misroute the document — it would just mislabel it for everyone
  // who reads the queue afterwards.
  const categoryOptions = CATEGORIES_BY_AUDIENCE[gap.audience] ?? CATEGORIES_BY_AUDIENCE.consumer;
  const [category, setCategory] = useState<KnowledgeCategory>(
    gap.categoryGuess && categoryOptions.includes(gap.categoryGuess)
      ? gap.categoryGuess
      : categoryOptions[0],
  );
  const [content, setContent] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);

  const canApprove = title.trim().length > 0 && content.trim().length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (decision === 'reject') {
        await knowledgeGapsApi.reject(gap.id, adminNote.trim() || undefined);
        toast.success('Gap dismissed');
      } else {
        if (!canApprove) {
          toast.error(null, 'Title and content are required to approve.');
          setSaving(false);
          return;
        }
        await knowledgeGapsApi.approve(gap.id, {
          title: title.trim(),
          category,
          content: content.trim(),
          adminNote: adminNote.trim() || undefined,
        });
        toast.success("Ingested — the AI's knowledge base has been updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err, 'Failed to resolve knowledge gap');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    height: 40,
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-800)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--neutral-700)',
  };

  return (
    <Modal isOpen onClose={onClose} title="Review knowledge gap" maxWidth={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--neutral-500)',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              marginBottom: 4,
            }}
          >
            User's question
          </div>
          <div style={{ fontSize: 14, color: 'var(--neutral-800)' }}>{gap.userQuestion}</div>
        </div>

        <div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--neutral-500)',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              marginBottom: 4,
            }}
          >
            Why the AI flagged it
          </div>
          <div style={{ fontSize: 13, color: 'var(--neutral-600)' }}>{gap.reason}</div>
        </div>

        {alreadyResolved ? (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={labelStyle}>Resolution:</span>
              <Badge variant={statusColors[gap.status]}>{formatCategory(gap.status)}</Badge>
            </div>
            {gap.adminResponse && (
              <div>
                <div style={labelStyle}>
                  {gap.status === 'approved' ? 'Ingested content' : 'Note'}
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 13,
                    color: 'var(--neutral-700)',
                    whiteSpace: 'pre-wrap',
                    background: 'var(--neutral-200)',
                    borderRadius: 'var(--radius-md)',
                    padding: 12,
                  }}
                >
                  {gap.adminResponse}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={labelStyle}>Decision</label>
              <select
                value={decision}
                onChange={(e) => setDecision(e.target.value as 'approve' | 'reject')}
                style={fieldStyle}
              >
                <option value="approve">Approve — add to AI knowledge base</option>
                <option value="reject">Reject — dismiss, no ingestion</option>
              </select>
            </div>

            {decision === 'approve' && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={labelStyle}>Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. VAT on bank transfer fees"
                    style={fieldStyle}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={labelStyle}>Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as KnowledgeCategory)}
                    style={fieldStyle}
                  >
                    {categoryOptions.map((c) => (
                      <option key={c} value={c}>
                        {formatCategory(c)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={labelStyle}>Answer to ingest</label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={6}
                    maxLength={20000}
                    placeholder="Write the correct, verified answer — this becomes what the AI tells future users who ask something similar."
                    style={{ ...fieldStyle, height: 'auto', resize: 'vertical', minHeight: 120 }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    Written verbatim — please verify accuracy before approving. This is what
                    the AI will state as fact to users going forward.
                  </span>
                </div>
              </>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={labelStyle}>Internal note (optional)</label>
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder="Not shown to users — for the admin record."
                style={{ ...fieldStyle, height: 'auto', resize: 'vertical', minHeight: 60 }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={saving}
                disabled={decision === 'approve' && !canApprove}
              >
                {decision === 'approve' ? 'Approve & ingest' : 'Reject'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
